/**
 * Phase 15 (F2) — picker that lists every entry in
 * `lib_live_stations` and copies the user's pick into
 * `lib_crew_mess_live_stations` as an independent SNAPSHOT (editing
 * the Crew Mess copy later does NOT affect the source).
 *
 * Backend endpoint already exists: POST
 * /api/lib/live_stations/{id}/copy-to-crew-mess. We just expose a
 * different UI affordance from the Crew Mess library page so the
 * user doesn't have to switch tabs to import a regular Live Station.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, ListPlus } from "lucide-react";
import { toast } from "sonner";
import {
  listLiveStations, copyLiveStationToCrewMess,
} from "@/api/mealPlanApi";

export default function ImportFromLiveStationsDialog({
  open, onOpenChange, onImported,
}) {
  const [loading, setLoading] = useState(false);
  const [stations, setStations] = useState([]);
  const [filter, setFilter] = useState("");
  const [pickingId, setPickingId] = useState(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    listLiveStations()
      .then((arr) => {
        if (!alive) return;
        setStations(arr || []);
      })
      .catch((e) => {
        if (!alive) return;
        toast.error(e?.response?.data?.detail || "Could not load Live Stations");
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return stations;
    return stations.filter((s) => (s.name || "").toLowerCase().includes(q));
  }, [stations, filter]);

  const handlePick = async (station) => {
    setPickingId(station.id);
    try {
      const dup = await copyLiveStationToCrewMess(station.id);
      toast.success(`Imported "${dup.name}" from Live Stations`);
      onImported?.(dup);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setPickingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        lockOutsideClick
        className="max-h-[80vh] overflow-hidden sm:max-w-lg flex flex-col"
        data-testid="import-from-live-stations-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListPlus className="h-5 w-5" />
            Import from Live Stations Library
          </DialogTitle>
          <DialogDescription>
            Pick a Live Station to copy into Crew Mess as a new entry.
            The copy is an independent <strong>snapshot</strong> — editing
            it later does NOT change the original.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 flex-1 overflow-hidden flex flex-col">
          <Input
            placeholder="Search by name…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="import-live-stations-filter"
          />
          <div
            className="flex-1 overflow-y-auto rounded border"
            data-testid="import-live-stations-list"
          >
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-12">
                {stations.length === 0
                  ? "No Live Stations in the library yet."
                  : `No matches for "${filter}".`}
              </p>
            )}
            {!loading && filtered.map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between border-b px-3 py-2 last:border-b-0 hover:bg-slate-50"
                data-testid={`import-live-station-row-${s.id}`}
              >
                <div className="min-w-0 flex-1 mr-2">
                  <div className="text-sm font-medium truncate">{s.name}</div>
                  {(s.description || "").trim() && (
                    <div className="text-[11px] text-muted-foreground truncate italic">
                      {s.description}
                    </div>
                  )}
                  <Badge variant="secondary" className="mt-0.5 text-[10px]">
                    {(s.dishes || []).length} dish
                    {(s.dishes || []).length === 1 ? "" : "es"}
                  </Badge>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={pickingId !== null}
                  onClick={() => handlePick(s)}
                  data-testid={`import-live-station-pick-${s.id}`}
                >
                  {pickingId === s.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    "Import"
                  )}
                </Button>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="import-live-stations-cancel"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
